/**
 * @lines98/client/lib/achievement-result-window — ПЛАВАЮЩЕЕ ОКОШКО открытых ачивок на
 * экране итогов: ненавязчивая карточка в ПРАВОМ ВЕРХНЕМ углу рамки доски (зона ниши),
 * поверх доски, скруглённые углы, БЕЗ рейла. Список открытых за партию ачивок: ИМЯ +
 * НАГРАДА (без медальона/описания). Появляется → висит ~3с → плавно исчезает.
 *
 * ЗАЧЕМ / WHY THIS EXISTS (owner-decisions 2026-06-22, тикет fe-achievement-result-window):
 * прежний UX (центральные тосты по одному + строки ачивок под золотом/осколками) owner
 * заменил ОДНИМ окошком над доской. Это переиспользуемый МОДУЛЬ (модель «ствол → ветки»,
 * owner-directive 2026-05-30): result-screen МОНТИРУЕТ его тонкой веткой, логика окна —
 * здесь, не в экране.
 *
 * Server-truth (I-3): сам факт разблокировки — серверный (`player_achievements`). Окошко
 * ВИЗУАЛИЗИРУЕТ `summary.progression.achievements_unlocked` (server-truth) — клиент НЕ
 * решает, открыта ли ачивка. Пусто/absent → окошка нет (no-op).
 *
 * Тайминг (owner-реш.3 «3 секунды», резолюция оркестратора п.2): СВОЙ токен
 * `ACHIEVEMENT_WINDOW_MS` === 3000 (старый `ACHIEVEMENT_TOAST_MS=2600` уехал с удалёнными
 * тостами — НЕ переиспользуется). reduce-motion (резолюция п.3, AC-7): DI-флаг `reduceMotion`
 * → появление/уход мгновенно (НЕ matchMedia внутри плеера — юнит-тестируемость).
 *
 * Overflow (owner-реш.4): окошко растёт ВНИЗ, но bounded к нижней кромке рамки доски; что
 * не влезло → строка «+ ещё N» (без скролла, без рейла). Сколько строк целиком vs N в
 * остаток — ЧИСТАЯ функция `planAchievementWindow(total, capacity)` (юнит-тестируема без
 * DOM, AC-10/11/18); capacity (сколько строк помещается) меряется от bounded-высоты доски.
 *
 * Позиция (AC-4, R10): относительно `.board-frame` (top-right), board-view НЕ модифицируется
 * (окошко — отдельный absolute-child рамки, только ЧИТАЕТ её геометрию).
 *
 * SCOPE: чистые `orderedAchievementIds` / `buildAchievementRows` / `planAchievementWindow`
 * (без DOM) + монтаж плавающей карточки над `.board-frame` (bounded-overflow + auto-dismiss +
 * плавный уход). NOT IN SCOPE: серверная логика ачивок (server-truth I-3); каталог имён/наград
 * (`lib/achievement-catalog`); формат награды (`lib/format.formatRewardPair`); рендер доски
 * (`lib/board-view`); точка-индикатор Профиля (`lib/achievement-seen-badge`).
 *
 * Cross-references:
 *   - lib/achievement-catalog.ts (achievementDisplay/achievementReward — имя+награда, R2/R4).
 *   - lib/format.ts (formatRewardPair — формат награды, R3, единый источник).
 *   - lib/board-view.ts (boardView.frame === .board-frame — якорь позиции, R10).
 *   - achievement-result-window.css (co-located — плавающая карточка, layout-plan, algebra).
 *   - тикет fe-achievement-result-window AC-1..-22 + owner-decisions 2026-06-22.
 */

import './achievement-result-window.css';

import type { MetaFaucetSummary } from '@lines98/shared/types/game';

import { ICON_GOLD, ICON_SHARD } from '../components/meta-icons.js';
import { achievementDisplay, achievementReward } from './achievement-catalog.js';
import { formatRuNumber } from './format.js';
import { t } from './i18n/index.js';

// =============================================================================
// Единый токен тайминга (AC-6, резолюция оркестратора п.2) — РОВНО 3000ms
// =============================================================================

/**
 * Длительность авто-показа окошка (ms) до плавного ухода. ЕДИНАЯ «ручка» тайминга
 * окошка на проект (меняется ТОЛЬКО здесь). СВОЙ токен (НЕ `ACHIEVEMENT_TOAST_MS` старых
 * тостов): owner сказал «3 секунды» (owner-реш.3) → 3000.
 */
export const ACHIEVEMENT_WINDOW_MS = 3000;

/** Длительность плавного появления/ухода окошка (ms) — composite-only (opacity+transform). */
const ANIMATE_MS = 220;

/** CSS-класс активной анимации появления/ухода (отсутствует при reduce-motion, AC-7). */
const ANIMATING_CLASS = 'achievement-window--animating';

// =============================================================================
// Чистые функции (юнит-тестируемо без DOM — achievement-result-window.test.ts)
// =============================================================================

/** Один элемент server-truth `achievements_unlocked` (открыто этой партией). */
interface AchievementUnlockEntry {
  readonly id: string;
  readonly tier_index?: number;
}

/** Строка окошка: ИМЯ + НАГРАДА (без описания/медальона, AC-2). */
export interface AchievementRow {
  /** Стабильный id ачивки (= achievementDisplay(id).id). */
  readonly id: string;
  /** Имя ачивки — must-visible label (achievementDisplay, R2; НЕ обрезается). */
  readonly name: string;
  /**
   * Награда — СУММА золото/осколки по под-тирам ачивки (achievementReward, R4). Рисуется
   * РЕАЛЬНЫМИ иконками ICON_GOLD/ICON_SHARD + число (owner-directive 2026-06-22: «вместо зол
   * и оск — реальные картинки», как плашка-подсказка ачивок в Профиле). Только ненулевые валюты.
   */
  readonly reward: { readonly gold: number; readonly shards: number };
}

/**
 * id ачивок в ДЕТЕРМИНИРОВАННОМ порядке (AC-3). Вход НЕ обязан быть отсортирован; дубли
 * (одна ачивка прилетела дважды) схлопываются (ОДНА строка на id). Порядок —
 * лексикографический по id (стабилен между прогонами). Defensive: не-массив / пусто /
 * мусорные элементы (без строкового непустого id) → []. Чистая функция (без DOM).
 *
 * @param unlocks — server-truth разблокировки этой партией (не отсортированы).
 */
export function orderedAchievementIds(unlocks: readonly AchievementUnlockEntry[]): string[] {
  if (!Array.isArray(unlocks)) return [];
  const seen = new Set<string>();
  for (const u of unlocks) {
    if (u && typeof u.id === 'string' && u.id.length > 0) seen.add(u.id);
  }
  return [...seen].sort((a, b) => (a < b ? -1 : a > b ? 1 : 0));
}

/**
 * Строки окошка (AC-2): одна на ачивку (дедуп по id, порядок = orderedAchievementIds).
 * Имя из каталога (R2); награда = formatRewardPair над СУММОЙ achievementReward по всем
 * под-тирам одной ачивки (многотировая ача → ОДНА строка, награда суммируется — как
 * прежний buildProgressionBlock). БЕЗ описания «как открыть» (owner-реш.2). Defensive:
 * не-массив / пусто → []. Чистая функция (без DOM).
 *
 * @param unlocks — server-truth разблокировки этой партией.
 */
export function buildAchievementRows(unlocks: readonly AchievementUnlockEntry[]): AchievementRow[] {
  if (!Array.isArray(unlocks)) return [];
  // Сумма награды по под-тирам каждого id (server-truth `(id, tier_index)` достаточно —
  // награда детерминированно выводится из каталога, тот же источник, что у сервера).
  const reward = new Map<string, { gold: number; shards: number }>();
  for (const u of unlocks) {
    if (!u || typeof u.id !== 'string' || u.id.length === 0) continue;
    const r = achievementReward(u.id, u.tier_index ?? 0);
    const prev = reward.get(u.id);
    if (prev) {
      prev.gold += r.gold;
      prev.shards += r.shards;
    } else {
      reward.set(u.id, { gold: r.gold, shards: r.shards });
    }
  }
  // Порядок строк — детерминированный (orderedAchievementIds), один на id.
  return orderedAchievementIds(unlocks).map((id) => {
    const sum = reward.get(id) ?? { gold: 0, shards: 0 };
    return { id, name: achievementDisplay(id).name, reward: { gold: sum.gold, shards: sum.shards } };
  });
}

/** Результат планирования окна: сколько строк целиком + сколько в «+ ещё N». */
export interface AchievementWindowPlan {
  /** Строк ачивок показано целиком. */
  readonly shown: number;
  /** Свёрнуто в строку «+ ещё N» (0 — переполнения нет, строки «+ ещё N» нет). */
  readonly more: number;
}

/**
 * ГЛАВНЫЙ ЯКОРЬ overflow-логики (AC-10/11/18). Из общего числа строк `total` и ёмкости
 * окна `capacity` (сколько строк помещается в bounded-высоту от доски) вернуть `shown`
 * (строк целиком) + `more` (свёрнуто в «+ ещё N»). Инварианты:
 *   • shown + more = total (ничего не теряется молча, §0.A);
 *   • total ≤ capacity → всё влезает → {shown: total, more: 0};
 *   • переполнение (total > capacity) → ОДИН слот ёмкости тратится на саму «+ ещё N» →
 *     shown = capacity − 1, more = total − shown;
 *   • capacity ≤ 1 → ни одна строка не влезает (даже под сводку) → {shown: 0, more: total}
 *     (AC-11 граничный случай: только «+ ещё N» со счётчиком ВСЕХ, не silent-crop);
 *   • монотонность: при той же total бОльшая capacity показывает не меньше строк;
 *   • defensive: отрицательные/NaN/0 → не падает, не отрицательно. Чистая функция (без DOM).
 *
 * @param total — всего строк ачивок к показу.
 * @param capacity — сколько строк помещается в bounded-высоту окна (≥0).
 */
export function planAchievementWindow(total: number, capacity: number): AchievementWindowPlan {
  // Defensive: NaN / отрицательное → 0 (не падаем, не отрицательно).
  const t = Number.isFinite(total) ? Math.max(0, Math.floor(total)) : 0;
  const cap = Number.isFinite(capacity) ? Math.max(0, Math.floor(capacity)) : 0;
  if (t === 0) return { shown: 0, more: 0 };
  if (t <= cap) return { shown: t, more: 0 }; // всё влезло
  // Переполнение: одна строка-слот уходит под «+ ещё N». capacity ≤ 1 → shown 0.
  const shown = Math.max(0, cap - 1);
  return { shown, more: t - shown };
}

// =============================================================================
// Монтаж окошка (DOM) — поведение проверяет achievement-result-window.viewport
// =============================================================================

/** Опции монтажа окошка разблокировки ачивок. */
export interface AchievementResultWindowOptions {
  /**
   * server-truth сводка партии (`MetaFaucetSummary`). Окошко читает из неё разблокировки
   * `summary.progression.achievements_unlocked` (server-truth, I-3 — клиент НЕ пересчитывает).
   */
  readonly summary: MetaFaucetSummary;
  /**
   * Рамка доски (`boardView.frame` === `.board-frame`) — якорь позиции (top-right, AC-4/R10).
   * Окошко добавляется как absolute-child рамки (board-view НЕ модифицируется).
   */
  readonly boardFrame: HTMLElement;
  /**
   * Мгновенный показ/уход без анимации (reduce-motion / тесты, AC-7). По умолчанию уважает
   * `prefers-reduced-motion`. DI (НЕ matchMedia внутри плеера — юнит-тестируемость).
   */
  readonly reduceMotion?: boolean;
  /**
   * Авто-уход по таймеру (по умолчанию true — реальный поток конца партии). Демо-роут
   * (`?demo=achievementwindow`) ставит false → окошко persistent для осмотра owner'ом
   * (AC-DEMO.3).
   */
  readonly autoDismiss?: boolean;
}

/** Handle смонтированного окошка — размонтирование экраном при уходе. */
export interface AchievementResultWindowHandle {
  /** Снять окошко немедленно (экран ушёл / unmount). Идемпотентно. */
  unmount(): void;
}

/** Учитывать ли prefers-reduced-motion (без window — тесты — «нет», опция важнее). */
function prefersReducedMotion(): boolean {
  if (typeof window === 'undefined' || !window.matchMedia) return false;
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

/**
 * Смонтировать окошко разблокировки ачивок над рамкой доски. Если разблокировок нет —
 * no-op (handle с пустым unmount, AC-1). Иначе показывает СПИСОК строк (имя+награда, AC-2)
 * РАЗОМ (не очередь), bounded к нижней кромке доски → остаток в «+ ещё N» (AC-9/10/11);
 * появляется плавно (reduce-motion → мгновенно, AC-7); авто-уход по `ACHIEVEMENT_WINDOW_MS`
 * (AC-6; demo autoDismiss:false → persistent).
 *
 * Bounded-capacity меряется ПОСЛЕ вставки в DOM: доступная высота окна (от верха окна до
 * нижней кромки рамки доски − delta) / высота одной строки = ёмкость. planAchievementWindow
 * раскладывает строки/остаток детерминированно (чистая функция).
 *
 * @param opts — server-truth сводка + рамка доски + reduce-motion + autoDismiss (DI).
 */
export function mountAchievementResultWindow(
  opts: AchievementResultWindowOptions,
): AchievementResultWindowHandle {
  // server-truth разблокировки этой партией (I-3 — читаем из сводки, не пересчитываем).
  const progression = opts.summary.progression as
    | { achievements_unlocked?: readonly AchievementUnlockEntry[] }
    | undefined;
  const rows = buildAchievementRows(progression?.achievements_unlocked ?? []);
  if (rows.length === 0) {
    return { unmount(): void {} };
  }

  const reduceMotion = opts.reduceMotion ?? prefersReducedMotion();
  const autoDismiss = opts.autoDismiss ?? true;
  const frame = opts.boardFrame;

  // Рамка доски — позиционный контейнер окна (absolute-child). board-frame в board.css
  // уже position:relative (плашка/баннер позиционируются в углах) — не ломаем, лишь
  // подстраховываемся: если вдруг static — окно «уплывёт». Делаем относительным мягко.
  if (getComputedStyle(frame).position === 'static') frame.style.position = 'relative';

  const win = document.createElement('div');
  win.className = 'achievement-window';
  win.setAttribute('role', 'status');
  win.setAttribute('aria-live', 'polite');
  win.setAttribute('aria-label', t('game.achWindow.unlockedAria'));
  // Появление: плавно (reduce-motion → мгновенно, без класса --animating, AC-7).
  if (!reduceMotion) win.classList.add(ANIMATING_CLASS);

  // Сперва рисуем ВСЕ строки — измерим высоту одной строки и доступную высоту → ёмкость.
  // Затем, если переполнение, перерисовываем по плану (shown строк + «+ ещё N»).
  const renderRows = (plan: AchievementWindowPlan): void => {
    win.textContent = '';
    // Заголовок «Достижения» (owner-directive 2026-06-22) — акцентным цветом + тонкий
    // разделитель (стиль плашки Профиля, «красиво»). Не строка-ачивка → в ёмкость строк не
    // считается (capacity меряется от верха ПЕРВОЙ .achievement-window-row).
    const title = document.createElement('div');
    title.className = 'achievement-window-title';
    title.textContent = t('game.achWindow.title');
    win.appendChild(title);
    const visible = plan.more > 0 ? rows.slice(0, plan.shown) : rows;
    for (const r of visible) {
      const row = document.createElement('div');
      row.className = 'achievement-window-row';
      const name = document.createElement('span');
      name.className = 'achievement-window-name';
      name.textContent = r.name;
      row.appendChild(name);
      // Награда — РЕАЛЬНЫЕ иконки (ICON_GOLD/ICON_SHARD) + число, только ненулевые валюты
      // (owner-directive 2026-06-22: «вместо зол и оск — картинки», отсылка к плашке Профиля
      // `badge-detail-reward-row`). Без текста «зол»/«оск».
      if (r.reward.gold > 0 || r.reward.shards > 0) {
        const reward = document.createElement('span');
        reward.className = 'achievement-window-reward';
        const parts: string[] = [];
        const aria: string[] = [];
        if (r.reward.gold > 0) {
          parts.push(
            `<span class="achievement-window-reward-pair">${ICON_GOLD}<span>${formatRuNumber(r.reward.gold)}</span></span>`,
          );
          aria.push(t('game.achWindow.rewardGold', { n: formatRuNumber(r.reward.gold) }));
        }
        if (r.reward.shards > 0) {
          parts.push(
            `<span class="achievement-window-reward-pair">${ICON_SHARD}<span>${formatRuNumber(r.reward.shards)}</span></span>`,
          );
          aria.push(t('game.achWindow.rewardShards', { n: formatRuNumber(r.reward.shards) }));
        }
        reward.innerHTML = parts.join('');
        reward.setAttribute('aria-label', t('game.achWindow.rewardAria', { parts: aria.join(', ') }));
        row.appendChild(reward);
      }
      win.appendChild(row);
    }
    if (plan.more > 0) {
      const more = document.createElement('div');
      more.className = 'achievement-window-more';
      more.textContent = t('game.achWindow.more', { n: plan.more });
      win.appendChild(more);
    }
  };

  /**
   * Замер ёмкости + финальный рендер по плану. КРИТИЧНО (баг silent-crop, тикет AC-9/AC-10):
   *
   * (1) НЕ опираться на win.getBoundingClientRect().height — она ЗАЖАТА css
   *     `max-height + overflow:hidden`: при overflow возвращает ОБРЕЗАННУЮ высоту окна, а не
   *     натуральную высоту контента. Прежняя формула (innerPadding = winRect.height − rowH·N)
   *     на этом ломалась: winRect.height ≪ rowH·N → innerPadding занулялся → available
   *     переоценивался → capacity завышалась → «+ ещё N» рисовалась НИЖЕ видимого низа окна и
   *     срезалась overflow:hidden. §0.A silent-crop + P1 must-visible.
   *
   * Ёмкость — чисто геометрически: доступная высота под СТРОКИ / (высота строки + gap). Строки
   * в flex-column разделены `gap`: k строк занимают rowH·k + gap·(k−1) ≤ available →
   * k ≤ (available + gap) / (rowH + gap). Слот под саму «+ ещё N» резервирует planAchievementWindow.
   *
   * available — геометрически от рамки доски: (низ рамки − delta − нижний паддинг) − (верх окна
   * + верхний паддинг). winRect.top надёжен (от клиппинга высоты НЕ зависит). Это тот же
   * источник, что css `max-height: 100% рамки − 2·--m` → совпадает с фактическим клиппом окна.
   *
   * (2) Замер делается ПОСЛЕ устаканивания раскладки (reflow на rAF + ResizeObserver рамки —
   *     ниже): на узкой доске (360×640) первый синхронный замер ловил ещё не финальную
   *     геометрию рамки → capacity переоценивалась → silent-crop. Пере-замер на осевшей
   *     геометрии это закрывает (scroll 89→0). (clientHeight как источник НЕ годится: у
   *     НЕпереполненного окна он = высоте контента, а не клиппу — занизил бы ёмкость до 0.)
   */
  const measureAndRender = (): void => {
    // Перед замером нужны ВСЕ строки (макс. высота контента → корректный клипп clientHeight).
    renderRows({ shown: rows.length, more: 0 });
    let capacity = rows.length;
    try {
      const frameRect = frame.getBoundingClientRect();
      const winRect = win.getBoundingClientRect();
      const firstRow = win.querySelector<HTMLElement>('.achievement-window-row');
      const rowH = firstRow ? firstRow.getBoundingClientRect().height : 0;
      const winStyle = getComputedStyle(win);
      // delta-зазор снизу = --m (как сверху/сбоку). Падаем мягко к rows.length при нулях.
      const delta = parseFloat(getComputedStyle(frame).getPropertyValue('--m')) || 0;
      const padTop = parseFloat(winStyle.paddingTop) || 0;
      const padBottom = parseFloat(winStyle.paddingBottom) || 0;
      const gap = parseFloat(winStyle.rowGap || winStyle.gap) || 0;
      // Доступно ПОД КОНТЕНТ строк = от верха ПЕРВОЙ строки (заголовок «Достижения» уже
      // ИСКЛЮЧЁН — он выше первой строки) до нижней кромки рамки − delta − нижний паддинг.
      // firstRow.top надёжен (от клиппинга высоты НЕ зависит) и учитывает высоту заголовка+gap.
      const rowsTop = firstRow ? firstRow.getBoundingClientRect().top : winRect.top + padTop;
      const available = frameRect.bottom - delta - padBottom - rowsTop;
      if (rowH > 0 && available > 0) {
        // rowH·k + gap·(k−1) ≤ available → k ≤ (avail+gap)/(rowH+gap).
        capacity = Math.max(0, Math.floor((available + gap) / (rowH + gap)));
      } else if (rowH > 0) {
        // Места под контент нет вовсе → ни одной строки (AC-11: только «+ ещё N» со всеми).
        capacity = 0;
      }
    } catch {
      capacity = rows.length; // замер не удался (jsdom/edge) → показать всё (P1 visibility).
    }
    // Финальный рендер по плану (всё влезает → plan.more=0 → без «+ ещё N»).
    renderRows(planAchievementWindow(rows.length, capacity));
  };

  // Первый замер+рендер сразу (мгновенная видимость), вставив окно в рамку доски.
  frame.appendChild(win);
  measureAndRender();

  let dismissTimer: number | null = null;
  let removeTimer: number | null = null;
  let raf: number | null = null;
  let ro: ResizeObserver | null = null;
  let disposed = false;
  let leaving = false;

  // Пере-замер после устаканивания раскладки + при ресайзе рамки доски (паттерн autofit-text):
  // на момент первого синхронного замера рамка доски могла быть ещё не в финальном размере
  // (viewport-sizer/боард ещё считаются) → capacity пере-вычисляется, когда геометрия осела.
  // Без этого узкая доска (360×640) показывала больше строк, чем влезает (silent-crop, §0.A).
  // Во время плавного ухода НЕ перерисовываем (иначе сбросили бы leaving-анимацию).
  const reflow = (): void => {
    if (disposed || leaving || !win.parentNode) return;
    measureAndRender();
  };
  if (typeof requestAnimationFrame !== 'undefined') {
    raf = requestAnimationFrame(reflow);
  }
  if (typeof ResizeObserver !== 'undefined') {
    ro = new ResizeObserver(reflow);
    ro.observe(frame);
  }

  const clearTimers = (): void => {
    if (dismissTimer !== null) {
      window.clearTimeout(dismissTimer);
      dismissTimer = null;
    }
    if (removeTimer !== null) {
      window.clearTimeout(removeTimer);
      removeTimer = null;
    }
    if (raf !== null) {
      cancelAnimationFrame(raf);
      raf = null;
    }
    if (ro) {
      ro.disconnect();
      ro = null;
    }
  };

  const removeNow = (): void => {
    clearTimers();
    if (win.parentNode) win.parentNode.removeChild(win);
  };

  /** Плавный уход → удаление (reduce-motion → мгновенно, AC-7). */
  const dismiss = (): void => {
    if (disposed) return;
    leaving = true; // стоп reflow на время плавного ухода (не сбросить leaving-анимацию).
    if (ro) {
      ro.disconnect();
      ro = null;
    }
    if (reduceMotion) {
      removeNow();
      return;
    }
    win.classList.add('achievement-window--leaving');
    removeTimer = window.setTimeout(removeNow, ANIMATE_MS);
  };

  // Авто-уход по таймеру (AC-6). demo (autoDismiss:false) → persistent (AC-DEMO.3).
  if (autoDismiss && typeof window !== 'undefined') {
    dismissTimer = window.setTimeout(dismiss, ACHIEVEMENT_WINDOW_MS);
  }

  return {
    unmount(): void {
      disposed = true;
      removeNow();
    },
  };
}
